import React, { useRef, useState } from 'react';
import HeaderNavigationButtonDesktop from 'components/commercetools-ui/organisms/header/header-navigation/header-navigation-desktop/header-navigation-button';
import { Tile } from 'components/commercetools-ui/organisms/header/types';
import useClassNames from 'helpers/hooks/useClassNames';
import useScrollDirection from 'helpers/hooks/useScrollDirection';
import MenuDropdown from './menu-dropdown';
import { Reference } from 'types/reference';
import Link from 'components/commercetools-ui/atoms/link';
import { Category } from 'shared/types/product';

export interface Props {
  links: Category[];
  tiles: Tile[];
  navigationTitleButtonEnabled?: boolean;
  navigationTitleButtonLabel?: string;
  navigationTitleButtonLink?: Reference;
}

const HeaderNavigationDesktop: React.FC<Props> = ({
  links,
  tiles,
  navigationTitleButtonEnabled,
  navigationTitleButtonLabel,
  navigationTitleButtonLink,
}) => {
  const [activeCategory, setActiveCategory] = useState<Category>();

  const tileContent = tiles.filter((tile) => tile.tileCategory === activeCategory?.name);

  const showSubMenu = (category?: Category) => {
    setActiveCategory(category);
  };

  const hideSubMenu = () => {
    setActiveCategory(undefined);
  };

  const showTimeout = useRef<NodeJS.Timeout | null>(null) as React.MutableRefObject<NodeJS.Timeout | null>;

  const clearShowTimeout = () => {
    if (showTimeout.current) {
      clearTimeout(showTimeout.current);
      showTimeout.current = null;
    }
  };

  const handleMouseIn = (category: Category) => {
    clearShowTimeout();
    showSubMenu(category);
  };

  const handleMouseOut = () => {
    clearShowTimeout();
    hideSubMenu();
  };

  const scrollDirection = useScrollDirection(5, -1);

  const containerClassName = useClassNames([
    'hidden lg:flex w-full justify-center border-t border-stroke-primary bg-white relative z-10',
  ]);

  const navigationClassNames = useClassNames([
    'container items-center flex w-fit transition-all duration-300 ease-in-out overflow-hidden',
    scrollDirection === 'down'
      ? 'max-h-0 opacity-0 transform translate-y-2 pointer-events-none'
      : 'max-h-300 opacity-100 transform translate-y-0 pointer-events-auto',
  ]);

  return (
    <div className={containerClassName} style={{ minHeight: scrollDirection === 'down' ? '0' : '48px' }}>
      {links && (
        <div className="relative w-full" onMouseLeave={handleMouseOut}>
          <nav
            aria-label="Global Navigation"
            className={navigationClassNames}
            style={{
              transformOrigin: 'bottom',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
            }}
          >
            <ol className="flex items-center justify-center gap-x-32 p-12 text-text-primary">
              <div className="flex items-center gap-24">
                {links.map((link) => (
                  <li key={link?.categoryId} onMouseEnter={() => handleMouseIn(link)}>
                    <HeaderNavigationButtonDesktop
                      show={link.categoryId === activeCategory?.categoryId}
                      link={link}
                      iconDirection={link.categoryId === activeCategory?.categoryId ? 'up' : 'down'}
                    />
                  </li>
                ))}
              </div>
              {navigationTitleButtonEnabled && navigationTitleButtonLabel && navigationTitleButtonLink && (
                <li onMouseEnter={hideSubMenu} className="flex">
                  <Link buttonVariant="primary" className="inline-block" link={navigationTitleButtonLink}>
                    {navigationTitleButtonLabel}
                  </Link>
                </li>
              )}
            </ol>
          </nav>

          {activeCategory && activeCategory.descendants && activeCategory.descendants.length > 0 && (
            <div className="absolute left-0 right-0 w-full" style={{ zIndex: 9 }}>
              <MenuDropdown
                show={true}
                onClick={hideSubMenu}
                links={activeCategory?.descendants ?? []}
                tileContent={tileContent[0]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderNavigationDesktop;
